import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { DContext } from "../../../context/Datacontext";
import {
    ChevronDown,
    ChevronUp,
    Users,
    Briefcase,
    Building2
} from "lucide-react";


export const CandidateProfiles = () => {
    const { BeURL } = useContext(DContext);
    const { jobId, recuriterId } = useParams();
    const [searchParams] = useSearchParams();
    const fromDate = searchParams.get("from");
    const toDate = searchParams.get("to");

    const [profiles, setProfiles] = useState([]);
    const [search] = useState("");
    const [expandedRow, setExpandedRow] = useState(null);
    const [loading,setLoading]=useState(false)

        const [page, setPage] = useState(1);
        const [limit, setLimit] = useState(10);

    const [pagination, setPagination] = useState({
        total: 0,
        totalPages: 1,
    });

    const fetchCandidateProfiles = useCallback(async () => {
        try {
            setLoading(true);

            const response = await fetch(
                `${BeURL}/candidate-profiles/${jobId}/${recuriterId}?fromDate=${fromDate}&toDate=${toDate}&page=${page}&limit=${ limit}&search=${encodeURIComponent(search)}`,
                {
                    credentials: "include",
                    method: "GET",
                }
            );

            const result = await response.json();

            if (result.success) {
                setProfiles(result.data);
                setPagination(result.pagination);
            } else {
                console.log(result.message);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [BeURL, jobId, fromDate,toDate, page, limit, search, recuriterId]);

    useEffect(() => {
        fetchCandidateProfiles();
    }, [fetchCandidateProfiles]);

    const firstRecord = profiles?.[0]?.records?.[0];

    const filteredProfiles = profiles.filter((item) => {
        const record = item.records?.[0];

        return (
            record?.candidate?.name
                ?.toLowerCase()
                .includes(search.toLowerCase()) ||
            item.email?.toLowerCase().includes(search.toLowerCase()) ||
            record?.candidate?.number?.includes(search)
        );
    });

    const toggleRow = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const getStatusColor = (status = "") => {
        status = status.toLowerCase();

        if (status.includes("joined") || status.includes("select"))
            return "bg-green-100 text-green-700";

        if (status.includes("reject") || status.includes("drop"))
            return "bg-red-100 text-red-700";

        if (status.includes("scheduled"))
            return "bg-blue-100 text-blue-700";

        if (status.includes("waiting"))
            return "bg-yellow-100 text-yellow-700";

        return "bg-gray-100 text-gray-700";
    };


    const totalPages = pagination?.totalPages || 1;



    const getPageNumbers = () => {
        const delta = 2;
        const range = [];
        const pages = [];

        range.push(1);

        for (
            let i = Math.max(2, page - delta);
            i <= Math.min(totalPages - 1, page + delta);
            i++
        ) {
            range.push(i);
        }

        if (totalPages > 1) {
            range.push(totalPages);
        }

        const uniqueRange = [...new Set(range)].sort((a, b) => a - b);

        let last = 0;

        uniqueRange.forEach((item) => {
            if (last) {
                if (item - last === 2) {
                    pages.push(last + 1);
                } else if (item - last > 2) {
                    pages.push("...");
                }
            }

            pages.push(item);
            last = item;
        });

        return pages;
    };


    const startRecord =
        pagination?.total === 0
            ? 0
            : (page - 1) * limit + 1;

    const endRecord = Math.min(
        page * limit,
        pagination?.total || 0
    );


    const goToFirst = () => {
        if (page > 1) {
            setPage(1);
        }
    };

    const goToPrevious = () => {
        if (page > 1) {
            setPage((prev) => prev - 1);
        }
    };

    const goToNext = () => {
        if (page < totalPages) {
            setPage((prev) => prev + 1);
        }
    };

    const goToLast = () => {
        if (page < totalPages) {
            setPage(totalPages);
        }
    };


    return (
        <div className="min-h-screen p-6">

            <div className="bg-white rounded-xl shadow border overflow-hidden">

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-2 bg-slate-50 border-b">

                    <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center">
                            <Users className="text-blue-600" size={14} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Candidate Profiles</p>
                            <h2 className="font-semibold">{pagination?.total || profiles.length}</h2>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 ">
                        <div className="flex items-center justify-center">
                            <Building2 className="text-orange-600" size={14} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Client</p>
                            <h2 className="truncate font-semibold">
                                {firstRecord?.client?.name || "-"}
                            </h2>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center">
                            <Briefcase className="text-green-600" size={14} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Job Title</p>
                            <h2 className="truncate font-semibold">
                                {firstRecord?.job?.title || "-"}
                            </h2>
                        </div>
                    </div>

                </div>

                <div className="overflow-auto max-h-[700px] cursor-grab">

                    <table className="w-full">

                        <thead className="bg-primary-500 text-white sticky top-0 z-10 text-xs">

                            <tr>

                                <th className="px-4 py-3 text-left">#</th>

                                <th className="px-4 py-3 text-left">
                                    Candidate
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Phone
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Email
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Team Lead
                                </th>

                                <th className="px-4 py-3 text-left">Submitted On</th>

                                <th className="px-4 py-3 text-left">Updated By</th>

                                <th className="px-4 py-3 text-left">
                                    Current Status
                                </th>

                                <th className="px-4 py-3 text-center">
                                    Action
                                </th>

                            </tr>

                        </thead>

                        <tbody>
                            {filteredProfiles.length > 0 ? (
                                filteredProfiles.map((group, index) => {
                                    const latestRecord = group.records[0];
                                    return (
                                        <React.Fragment key={index}>

                                            {/* Main Row */}

                                            <tr className="border-b hover:bg-slate-50">

                                                <td className="px-4 py-3 font-medium">
                                                    {index + 1}
                                                </td>

                                                <td className="px-4 py-3">
                                                    <div className="font-semibold text-slate-800">
                                                        {latestRecord?.candidate?.name}
                                                    </div>
                                                </td>

                                                <td className="px-4 py-3">
                                                    {latestRecord?.candidate?.number}
                                                </td>

                                                <td className="px-4 py-3">
                                                    {group.email}
                                                </td>

                                                <td className="px-4 py-3">
                                                    {latestRecord?.reportingTeamLead?.name || "-"}
                                                </td>

                                                <td className="px-4 py-3">
                                                    {latestRecord?.profileSubmittedOn
                                                        ? new Date(latestRecord.profileSubmittedOn).toLocaleDateString()
                                                        : "—"}
                                                </td>
                                                <td className="px-4 py-3">{latestRecord?.submittedBy?.name}</td>

                                                <td className="px-4 py-3">

                                                    <span
                                                        className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                                                            latestRecord?.recentStatus?.status
                                                        )}`}
                                                    >
                                                        {latestRecord?.recentStatus?.status}
                                                    </span>

                                                </td>

                                                <td className="px-4 py-3 text-center">
                                                    <button
                                                        onClick={() => toggleRow(index)}
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition
                                                        ${expandedRow === index
                                                                ? "bg-red-50 text-red-600 hover:bg-red-100"
                                                                : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                                                            }`}
                                                    >
                                                        {expandedRow === index ? (
                                                            <>
                                                                <ChevronUp size={16} />
                                                                Close
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ChevronDown size={16} />
                                                                Open
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>

                                            {/* Expanded Status History */}
                                            {expandedRow === index && (

                                                <tr className="bg-slate-50">

                                                    <td colSpan={8} className="p-5">

                                                        <div className="border rounded-xl overflow-hidden">

                                                            <div className="bg-slate-100 px-4 py-3 border-b flex justify-between">

                                                                <h3 className="font-semibold">
                                                                    Status History
                                                                </h3>

                                                                <span className="text-sm text-gray-500">
                                                                    {group.records.length} Records
                                                                </span>

                                                            </div>

                                                            <table className="w-full">

                                                                <thead className="bg-gray-200">

                                                                    <tr>

                                                                        <th className="text-left px-4 py-3">
                                                                            Status Changed On
                                                                        </th>

                                                                        <th className="text-left px-4 py-3">
                                                                            Updated By
                                                                        </th>

                                                                        <th className="text-left px-4 py-3">
                                                                            Status
                                                                        </th>

                                                                    </tr>

                                                                </thead>

                                                                <tbody>

                                                                    {group.records.map((record) => (

                                                                        <tr
                                                                            key={record._id}
                                                                            className="border-t hover:bg-white"
                                                                        >


                                                                            <td className="px-4 py-3">
                                                                                {record?.recentStatus?.timestamp
                                                                                    ? new Date(record?.recentStatus?.timestamp).toLocaleDateString()
                                                                                    : "—"}
                                                                            </td>

                                                                            <td className="px-4 py-3">
                                                                                {record.submittedBy?.name}
                                                                            </td>

                                                                            <td className="px-4 py-3">

                                                                                <span
                                                                                    className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                                                                                        record.recentStatus?.status
                                                                                    )}`}
                                                                                >
                                                                                    {record.recentStatus?.status}
                                                                                </span>

                                                                            </td>

                                                                        </tr>

                                                                    ))}

                                                                </tbody>

                                                            </table>

                                                        </div>

                                                    </td>

                                                </tr>

                                            )}

                                        </React.Fragment>
                                    );
                                })
                            ) : (

                                <tr>

                                    <td
                                        colSpan={8}
                                        className="text-center py-10 text-gray-500"
                                    >
                                        No Candidate Profiles Found
                                    </td>

                                </tr>

                            )}
                        </tbody>

                    </table>

                </div>


                <div className="flex flex-col lg:flex-row items-center justify-between gap-5 px-6 py-4 bg-white border-t">

                    {/* Showing Records */}
                    <div className="text-sm text-gray-600">
                        Showing{" "}
                        <span className="font-semibold text-primary-600">
                            {startRecord}
                        </span>
                        {" - "}
                        <span className="font-semibold text-primary-600">
                            {endRecord}
                        </span>
                        {" "}of{" "}
                        <span className="font-semibold text-primary-600">
                            {pagination?.total || 0}
                        </span>{" "}
                        Positions
                    </div>

                    <div className="flex flex-wrap items-center gap-5">

                        {/* Rows Per Page */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                                Rows per page
                            </span>

                            <select
                                value={limit}
                                onChange={(e) => {
                                    setLimit(Number(e.target.value));
                                    setPage(1);
                                }}
                                disabled={loading}
                                className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none disabled:opacity-50"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center gap-2">

                            {/* First */}
                            <button
                                onClick={goToFirst}
                                disabled={page === 1 || loading}
                                className="px-3 py-2 rounded-lg border hover:bg-gray-100 disabled:opacity-40"
                            >
                                ⏮
                            </button>

                            {/* Previous */}
                            <button
                                onClick={goToPrevious}
                                disabled={page === 1 || loading}
                                className="px-3 py-2 rounded-lg border hover:bg-gray-100 disabled:opacity-40"
                            >
                                ◀
                            </button>

                            {/* Page Numbers */}
                            {getPageNumbers().map((item, index) =>
                                item === "..." ? (
                                    <span
                                        key={`dots-${index}`}
                                        className="px-2 text-gray-500"
                                    >
                                        ...
                                    </span>
                                ) : (
                                    <button
                                        key={`page-${item}`}
                                        onClick={() => setPage(item)}
                                        disabled={loading}
                                        className={`w-10 h-10 rounded-lg font-semibold transition
                                             ${page === item
                                                ? "bg-primary-600 text-white shadow-lg"
                                                : "border bg-white hover:bg-primary-50"
                                            }`}
                                    >
                                        {item}
                                    </button>
                                )
                            )}

                            {/* Next */}
                            <button
                                onClick={goToNext}
                                disabled={page === totalPages || loading}
                                className="px-3 py-2 rounded-lg border hover:bg-gray-100 disabled:opacity-40"
                            >
                                ▶
                            </button>

                            {/* Last */}
                            <button
                                onClick={goToLast}
                                disabled={page === totalPages || loading}
                                className="px-3 py-2 rounded-lg border hover:bg-gray-100 disabled:opacity-40"
                            >
                                ⏭
                            </button>

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );
};